import FeatureGateClient from '@atlaskit/feature-gate-js-client/client';
import {
    FeatureGateEnvironment,
    Identifiers,
    CustomAttributes,
    PerimeterType
} from '@atlaskit/feature-gate-js-client';
import { get } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import Logger from '../logger';
import { setBooleanFeatureFlagResolver } from '@atlaskit/platform-feature-flags';

interface EnvConfig {
    apiKey: string;
    environment: FeatureGateEnvironment;
    perimeter: PerimeterType;
    targetApp: string;
}

const TARGET_APP = 'sac-infrastructure_web';
const DEFAULT_OPTIONS = {
    perimeter: PerimeterType.COMMERCIAL,
    targetApp: TARGET_APP
};

export let statsigInitialized = false;
let statsigClient: FeatureGateClient;
let initializationPromise: Promise<boolean> | null = null;

// Reset function for testing
export const resetStatsigClient = () => {
    statsigInitialized = false;
    statsigClient = undefined as any;
    initializationPromise = null;
};

const getEnvironmentConfig = (): EnvConfig => {
    const environment: 'prod' | 'staging' | 'dev' | string =
        get(window, 'MICROS_ENVTYPE') || 'dev';
    const STATSIG_CLIENT_API_KEY = get(window, 'STATSIG_CLIENT_API_KEY', '');

    switch (environment) {
        case 'prod':
            return {
                ...DEFAULT_OPTIONS,
                apiKey: STATSIG_CLIENT_API_KEY,
                environment: FeatureGateEnvironment.Production
            };
        case 'staging':
            return {
                ...DEFAULT_OPTIONS,
                apiKey: STATSIG_CLIENT_API_KEY,
                environment: FeatureGateEnvironment.Staging
            };
        case 'dev':
        default:
            return {
                ...DEFAULT_OPTIONS,
                apiKey: STATSIG_CLIENT_API_KEY,
                environment: FeatureGateEnvironment.Development
            };
    }
};

export const getAnalyticsAnonymousId = (): string => {
    if (!window?.localStorage) {
        return uuidv4();
    }

    let stableId = window.localStorage.getItem('STATSIG_STABLE_ID');

    if (!stableId) {
        stableId = uuidv4();
        window.localStorage.setItem('STATSIG_STABLE_ID', stableId);
    }

    return stableId.replace(/"/g, '');
};

const configurePlatformFeatureFlags = () => {
    setBooleanFeatureFlagResolver((flagKey: string) => {
        return statsigClient.checkGate(flagKey);
    });
};

const hasValidIdentifier = (identifiers: Identifiers): boolean => {
    if (!identifiers) {
        return false;
    }

    return Object.values(identifiers).some((value) => !!value);
};

export const initialiseStatsigClient = async (
    identifiers?: Identifiers,
    customAttributes?: CustomAttributes
): Promise<boolean> => {
    if (
        statsigClient?.initializeCalled() &&
        statsigClient?.initializeCompleted()
    ) {
        return true;
    }

    if (initializationPromise) {
        return initializationPromise;
    }

    initializationPromise = (async () => {
        try {
            const identifiersCopy = identifiers ? { ...identifiers } : {};

            if (!hasValidIdentifier(identifiersCopy)) {
                identifiersCopy.analyticsAnonymousId =
                    getAnalyticsAnonymousId();
            }

            if (!customAttributes) {
                customAttributes = {
                    analyticsAnonymousId: getAnalyticsAnonymousId()
                };
            }

            const envConfig = getEnvironmentConfig();

            if (!envConfig.apiKey) {
                statsigInitialized = false;
                Logger.error(
                    '[sac-infra] STATSIG client Initialization Error , Missing Client Key'
                );
                return false;
            }

            if (!statsigClient || !statsigClient.initializeCalled()) {
                statsigClient = new FeatureGateClient();
            }

            await statsigClient.initialize(
                envConfig,
                identifiersCopy,
                customAttributes
            );
            statsigInitialized = true;
            Logger.info('[sac-infra] STATSIG client Initialized');
            configurePlatformFeatureFlags();
            Logger.info('[sac-infra] Platform Feature Flags Configured');
            return true;
        } catch (error) {
            statsigInitialized = false;
            Logger.error(
                { error },
                '[sac-infra] STATSIG client Initialization ERROR'
            );
            return false;
        } finally {
            initializationPromise = null;
        }
    })();

    return initializationPromise;
};

const ensureInitialized = async (
    identifiers: Identifiers,
    customAttributes?: CustomAttributes
): Promise<boolean> => {
    if (statsigInitialized && statsigClient?.initializeCompleted()) {
        return true;
    }

    return await initialiseStatsigClient(identifiers, customAttributes);
};

export const getFeatureGate = async (
    gateKey: string,
    gateDefaultValue: boolean,
    identifiers?: Identifiers,
    customAttributes?: CustomAttributes
    // eslint-disable-next-line max-params
) => {
    try {
        const identifiersCopy = identifiers ? { ...identifiers } : {};

        if (!hasValidIdentifier(identifiersCopy)) {
            identifiersCopy.analyticsAnonymousId = getAnalyticsAnonymousId();
        }

        const isInitialized = await ensureInitialized(
            identifiersCopy,
            customAttributes
        );
        if (!isInitialized) {
            return gateDefaultValue;
        }

        // Ensure statsigClient exists - it should be set by initialiseStatsigClient
        if (!statsigClient) {
            Logger.error(
                '[sac-infra] STATSIG client ERROR: Client not initialized'
            );
            return gateDefaultValue;
        }

        if (Object.keys(identifiersCopy).length > 0) {
            await statsigClient.updateUser(
                getEnvironmentConfig(),
                identifiersCopy,
                customAttributes
            );
        }

        return statsigClient.checkGate(gateKey);
    } catch (error) {
        Logger.error({ error }, '[sac-infra] STATSIG client ERROR');
        return gateDefaultValue;
    }
};

export const getExperiment = async (
    experimentName: string,
    identifiers: Identifiers = {},
    customAttributes?: CustomAttributes
) => {
    try {
        const identifiersCopy = { ...identifiers };

        if (!hasValidIdentifier(identifiersCopy)) {
            identifiersCopy.analyticsAnonymousId = getAnalyticsAnonymousId();
        }

        const isInitialized = await ensureInitialized(
            identifiersCopy,
            customAttributes
        );
        if (!isInitialized || !statsigClient) {
            throw new Error(
                'Client must be initialized before using this method'
            );
        }

        await statsigClient.updateUser(
            getEnvironmentConfig(),
            identifiersCopy,
            customAttributes
        );
        return statsigClient.getExperiment(experimentName);
    } catch (error) {
        Logger.error(
            { error, experimentName },
            '[sac-infra] STATSIG client ERROR in getExperiment'
        );
        throw error;
    }
};
